import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import { DEFAULT_PAGE_SIZE } from "commons/constants";
import { mapToDistinctOptionItems } from "commons/ultilities/option-item/option-item-ultil";
import type {
  PaginationSearchRequest,
  PaginationSearchResult,
} from "share-components/mui-wrap/auto-complete/x-auto-complete-with-pagination";

const isSameText = (left: string, right: string) => {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
};

export type WorkOrderPoFilterSelection = {
  purchaseOrderId: string;
  statusId: string;
  wONumber: string;
  wOBatchNumber: string;
};

const getWorkOrderPoFilterOptions = async (
  keyword: string,
  request: PaginationSearchRequest,
  filters: Partial<WorkOrderPoFilterSelection>,
) => {
  return workOrderPoBusiness.getLists({
    filter: keyword,
    maxResultCount: request.pageSize,
    skipCount: request.pageIndex * request.pageSize,
    sorting: "",
    customerId: "",
    purchaseOrderId: filters.purchaseOrderId ?? "",
    statusId: filters.statusId ?? "",
    wONumber: filters.wONumber ?? "",
    wOBatchNumber: filters.wOBatchNumber ?? "",
  });
};

const hasAnyMatchedWorkOrderPo = async (
  filters: Partial<WorkOrderPoFilterSelection>,
) => {
  const res = await getWorkOrderPoFilterOptions(
    "",
    {
      pageIndex: 0,
      pageSize: 1,
    },
    filters,
  );

  return (res.totalCount ?? 0) > 0;
};

export const searchPurchaseOrderOptionsWithPagination = async (
  keyword: string,
  request: PaginationSearchRequest,
  selectedStatusId: string,
  selectedWoNumber: string,
  selectedWoBatchNumber: string,
): Promise<PaginationSearchResult> => {
  const res = await getWorkOrderPoFilterOptions(keyword, request, {
    statusId: selectedStatusId,
    wONumber: selectedWoNumber,
    wOBatchNumber: selectedWoBatchNumber,
  });

  return {
    items: mapToDistinctOptionItems(
      res.items,
      (x) => x.purchaseOrderId,
      (x) => x.purchaseOrder?.poNumber,
    ),
    totalCount: res.totalCount,
  };
};

export const searchWoNumberOptionsWithPagination = async (
  keyword: string,
  request: PaginationSearchRequest,
  selectedPurchaseOrderId: string,
  selectedStatusId: string,
  selectedWoBatchNumber: string,
): Promise<PaginationSearchResult> => {
  const res = await getWorkOrderPoFilterOptions(keyword, request, {
    purchaseOrderId: selectedPurchaseOrderId,
    statusId: selectedStatusId,
    wOBatchNumber: selectedWoBatchNumber,
  });

  return {
    items: mapToDistinctOptionItems(res.items, (x) => x.woNumber),
    totalCount: res.totalCount,
  };
};

export const searchWoBatchNumberOptionsWithPagination = async (
  keyword: string,
  request: PaginationSearchRequest,
  selectedPurchaseOrderId: string,
  selectedStatusId: string,
  selectedWoNumber: string,
): Promise<PaginationSearchResult> => {
  const res = await getWorkOrderPoFilterOptions(keyword, request, {
    purchaseOrderId: selectedPurchaseOrderId,
    statusId: selectedStatusId,
    wONumber: selectedWoNumber,
  });

  return {
    items: mapToDistinctOptionItems(res.items, (x) => x.woBatchNumber),
    totalCount: res.totalCount,
  };
};

export const isWoNumberCompatibleWithBatch = async (
  woNumber: string,
  woBatchNumber: string,
): Promise<boolean> => {
  if (!woNumber || !woBatchNumber) return true;

  const res = await searchWoBatchNumberOptionsWithPagination(
    woBatchNumber,
    {
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    "",
    "",
    woNumber,
  );

  return res.items?.some((x) => isSameText(x.value, woBatchNumber)) ?? false;
};

export const isWoNumberCompatibleWithSelection = async (
  selection: WorkOrderPoFilterSelection,
): Promise<boolean> => {
  if (!selection.wONumber) return true;

  return hasAnyMatchedWorkOrderPo({
    purchaseOrderId: selection.purchaseOrderId,
    statusId: selection.statusId,
    wONumber: selection.wONumber,
    wOBatchNumber: selection.wOBatchNumber,
  });
};

export const isWoBatchNumberCompatibleWithWo = async (
  woBatchNumber: string,
  woNumber: string,
): Promise<boolean> => {
  if (!woNumber || !woBatchNumber) return true;

  const res = await searchWoNumberOptionsWithPagination(
    woNumber,
    {
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    "",
    "",
    woBatchNumber,
  );

  return res.items?.some((x) => isSameText(x.value, woNumber)) ?? false;
};

export const isWoBatchNumberCompatibleWithSelection = async (
  selection: WorkOrderPoFilterSelection,
): Promise<boolean> => {
  if (!selection.wOBatchNumber) return true;

  return hasAnyMatchedWorkOrderPo({
    purchaseOrderId: selection.purchaseOrderId,
    statusId: selection.statusId,
    wONumber: selection.wONumber,
    wOBatchNumber: selection.wOBatchNumber,
  });
};

export const isPurchaseOrderCompatibleWithSelection = async (
  selection: WorkOrderPoFilterSelection,
): Promise<boolean> => {
  if (!selection.purchaseOrderId) return true;

  return hasAnyMatchedWorkOrderPo({
    purchaseOrderId: selection.purchaseOrderId,
    statusId: selection.statusId,
    wONumber: selection.wONumber,
    wOBatchNumber: selection.wOBatchNumber,
  });
};

export const isStatusCompatibleWithSelection = async (
  selection: WorkOrderPoFilterSelection,
): Promise<boolean> => {
  if (!selection.statusId) return true;

  return hasAnyMatchedWorkOrderPo({
    purchaseOrderId: selection.purchaseOrderId,
    statusId: selection.statusId,
    wONumber: selection.wONumber,
    wOBatchNumber: selection.wOBatchNumber,
  });
};
